import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Sparkles, Boxes, ListChecks } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/v2/PageHeader";
import { PlanUsageBanner } from "@/components/v2/PlanUsageBanner";
import { StatCard } from "@/components/v2/StatCard";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { EmptyState } from "@/components/v2/EmptyState";
import { useToast } from "@/components/v2/Toast";

/**
 * Services — Pencil frame `IikYv`. Table layout with KPI strip on top.
 * Row click navigates to `/services/:id` (the new Service Detail page —
 * `Qp9lm`) where the operator can manage Bookings / Clients / Inventory /
 * Custom Fields / Settings.
 */

type ServiceRow = {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  orderIndex: number;
};

const createSchema = z.object({
  name: z.string().min(2, "Name too short"),
  description: z.string().optional(),
});
type CreateFormValues = z.infer<typeof createSchema>;

export function Services() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);

  const list = useQuery({
    queryKey: ["services"],
    enabled: !!accessToken,
    queryFn: async () => unwrap(await api.GET("/api/v1/services", {})),
  });

  // KPI strip: Total Services / Active Bookings / Linked Clients / Inventory.
  // Bookings + clients counts use the existing list endpoints (cheap on dev).
  const allBookings = useQuery({
    queryKey: ["bookings", "all"],
    enabled: !!accessToken,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings", { params: { query: { page: 0, size: 1 } } }),
      ),
  });

  const allClients = useQuery({
    queryKey: ["customers", "all"],
    enabled: !!accessToken,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/customers", { params: { query: { page: 0, size: 1 } } }),
      ),
  });

  const inventory = useQuery({
    queryKey: ["inventory.overview"],
    enabled: !!accessToken,
    queryFn: async () => unwrap(await api.GET("/api/v1/inventory", {})),
  });

  const create = useMutation({
    mutationFn: async (body: CreateFormValues) =>
      unwrap(await api.POST("/api/v1/services", { body: { ...body, active: true } })),
    onSuccess: (created) => {
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success("Service created");
      if (created?.id) navigate(`/services/${created.id}`);
    },
    onError: (e) => {
      const msg = (e as Error).message;
      if (msg.includes("plan_service_limit_exceeded")) {
        toast.error("Service limit reached", "Contact support to increase your limit.");
        return;
      }
      toast.error("Couldn't create service", msg);
    },
  });

  const services = (list.data ?? []) as ServiceRow[];

  const cols: Column<ServiceRow>[] = [
    {
      key: "name",
      header: "Service Name",
      cell: (r) => (
        <div className="min-w-0">
          <p className="font-medium text-sm leading-tight truncate">{r.name}</p>
          {r.description && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {r.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortAccessor: (r) => (r.active ? 0 : 1),
      cell: (r) => <StatusBadge label={r.active ? "active" : "draft"} />,
    },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Services"
        subtitle="Manage and configure your service catalog. Click a row to edit details, custom fields, or view bookings."
        actions={
          <Button
            size="sm"
            onClick={() => setShowCreate((s) => !s)}
          >
            {showCreate ? "Cancel" : "+ New service"}
          </Button>
        }
      />

      <PlanUsageBanner />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total services"
          value={services.length}
          hint={
            services.filter((s) => s.active).length === services.length
              ? "All active"
              : `${services.filter((s) => s.active).length} active`
          }
        />
        <StatCard
          label="Active bookings"
          value={allBookings.data?.totalElements ?? 0}
          hint="Across all services"
        />
        <StatCard
          label="Linked clients"
          value={allClients.data?.totalElements ?? 0}
          hint="Distinct customers"
        />
        <StatCard
          label="Inventory items"
          value={inventory.data?.items?.length ?? 0}
          hint="Plans + physical"
        />
      </div>

      {showCreate && <CreateForm onSubmit={(v) => create.mutate(v)} pending={create.isPending} />}

      {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {list.isError && (
        <p className="text-sm text-destructive">{(list.error as Error).message}</p>
      )}

      {list.data && (
        <DataTable
          columns={cols}
          rows={services}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/services/${r.id}`)}
          sortable
          resizable
          hideable
          searchable
          searchPlaceholder="Search services…"
          pagination="client"
          pageSize={30}
          exportCsv={{
            filename: `services-${new Date().toISOString().slice(0, 10)}.csv`,
          }}
          emptyState={
            <div className="p-8">
              <EmptyState
                icon={Sparkles}
                title="No services yet"
                description="Create your first service so customers can book it from book.booqly.co/your-slug."
                action={
                  <Button onClick={() => setShowCreate(true)}>+ Add a service</Button>
                }
              />
            </div>
          }
        />
      )}
    </div>
  );
}

function CreateForm({
  onSubmit,
  pending,
}: {
  onSubmit: (v: CreateFormValues) => void;
  pending: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
  });

  return (
    <div className="rounded-xl border bg-card p-5">
      <p className="text-sm font-semibold tracking-tight">New service</p>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">
        A service groups what customers book — pricing comes from the inventory
        you link to it. Open the new service after creation to add custom
        fields, change visibility, or scope inventory.
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="name">Name</Label>
          <Input id="name" placeholder="Photoshoot — 1 hour" {...register("name")} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            placeholder="Studio session, edited photos within 48h"
            {...register("description")}
          />
        </div>
        <div>
          <Button type="submit" disabled={pending} className="w-full">
            {pending ? "Creating…" : "Create service"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Silence unused — Boxes / ListChecks reserved for the per-service KPI cards
// once the backend exposes per-service counts in a roll-up endpoint.
void Boxes;
void ListChecks;
